import { el, btn, backButton, menuCard, MENU_ICONS } from '@/ui/dom';
import { MM_TOUR_LEN } from '@/net/matchmaking';
import { parseJoinCode } from '@/net/lobby';
import { lockCta, type LockCtaOptions } from '@/ui/lockCta';

/**
 * OnlineScreen.ts — the "PLAY ONLINE" hub (M30/M34/M44). Three live paths:
 *   - Start a Tour  — matchmade 5-city tour with strangers          (M44)
 *   - Quick Match   — matchmade quick race with strangers, zero-choice (M44)
 *   - Challenge Friends — friend-invite rooms → a tour OR a quick race (M30/M33)
 * plus JOIN WITH CODE (M63): type/paste a friend's room code (or their whole
 * invite link) to walk straight into their race lobby.
 * Matchmaking pools searchers in a ~10 s window; slots no human fills are taken
 * by AI presented EXACTLY like found players — seamless, never revealed
 * (owner-locked).
 */
export interface OnlineCallbacks {
  onStartTour: () => void;
  onQuickMatch: () => void;
  onChallengeFriend: () => void;
  /** M63: a validated friend room code was submitted — join its lobby. */
  onJoinCode: (code: string) => void;
  /** M140: RACE CREW — your crew, friends and crew races. */
  onRaceCrew?: () => void;
  onBack: () => void;
}

/** M140: the crew card's shield-and-helmet glyph. */
const CREW_ICON =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 1.8 21 4.6v6.1c0 5.5-3.8 9.9-9 11.5-5.2-1.6-9-6-9-11.5V4.6z" opacity=".32"/>' +
  '<path d="M12 4.2c-3.2 0-5.4 2.3-5.4 5.3 0 1 .3 1.9.6 2.6l-.4 1.9 2-.4c.4.2.9.3 1.4.3h5.5c1 0 1.7-.8 1.7-1.7V9.5c0-3-2.2-5.3-5.4-5.3Zm4 5.2h-3.6c-.6 0-1.1.5-1.1 1.1 0 .6.5 1.1 1.1 1.1H16v.6c0 .3-.2.5-.5.5h-3.2c-1.3 0-2.3-1-2.3-2.2s1-2.2 2.3-2.2h3.5c.1.4.2.7.2 1.1Z"/>' +
  '<path d="M7.5 16.6h9v1.6h-9z" opacity=".8"/></svg>';

export class OnlineScreen {
  private root: HTMLDivElement;
  private codeInput: HTMLInputElement;
  private head: HTMLElement;
  private lockEl: HTMLDivElement | null = null;
  private crewBadge: HTMLSpanElement | null = null;

  constructor(
    parent: HTMLElement,
    private cb: OnlineCallbacks,
  ) {
    // M113: photo-backed (public/online/bg.webp, the night-highway painting)
    // with a left-weighted scrim — the cine grade over the live world is gone.
    this.root = el('div', 'screen screen-online screen-onlinebg', parent);
    this.root.style.display = 'none';

    // Full-bleed editorial column (reference art) — no glass box; the cards
    // (strengthened glass since M113) carry the contrast over the photo.
    const panel = el('div', 'online-panel', this.root);
    const head = el('div', 'mhead', panel);
    this.head = head;
    const logotype = el('div', 'mhead-logotype', head);
    logotype.innerHTML =
      '<span class="mhead-chrome">PLAY</span> <span class="mhead-gold">ONLINE</span>';
    el('div', 'mhead-sub', head).textContent = 'RACE REAL PEOPLE OVER THE INTERNET';

    const buttons = el('div', 'online-buttons', panel);

    menuCard(
      {
        icon: MENU_ICONS.bolt,
        title: 'QUICK MATCH',
        sub: 'FIND A RACE NOW',
        desc: 'Jump in and race against online drivers',
        className: 'mcard-primary',
      },
      buttons,
      () => this.cb.onQuickMatch(),
    );
    // M113: the secondary cards wear GOLD accents over the photo (the owner's
    // black-and-gold cinema) — the old blue/purple tints read off-brand there.
    menuCard(
      {
        icon: MENU_ICONS.globe,
        title: 'START A TOUR',
        sub: `${MM_TOUR_LEN}-CITY CHAMPIONSHIP VS THE WORLD`,
        desc: `Compete in a ${MM_TOUR_LEN}-city tour and become the champion`,
        accent: '#f4c542',
      },
      buttons,
      () => this.cb.onStartTour(),
    );
    menuCard(
      {
        icon: MENU_ICONS.people,
        title: 'CHALLENGE FRIENDS',
        sub: 'RACE A FRIEND OR CREATE A PRIVATE RACE',
        desc: 'Invite friends or join a private room',
        accent: '#f4c542',
      },
      buttons,
      () => this.cb.onChallengeFriend(),
    );
    // M140: RACER CLUB (named RACE CREW until M141) — the social home
    // (your club, friends, club races).
    if (this.cb.onRaceCrew) {
      const crew = menuCard(
        {
          icon: CREW_ICON,
          title: 'RACER CLUB',
          sub: 'YOUR CLUB, FRIENDS + CLUB RACES',
          desc: 'Start a club, rank up together and call them into races',
          accent: '#f4c542',
          className: 'mcard-crew',
        },
        buttons,
        () => this.cb.onRaceCrew?.(),
      );
      this.crewBadge = el('span', 'mcard-badge', crew);
      this.crewBadge.style.display = 'none';
    }

    // JOIN WITH CODE (M63): the missing half of "share the room code" — a
    // friend without the link types the 4-letter code (a pasted link works too).
    // Reference art: a diamond-tipped rule divider, a lock-badged input and a
    // gold JOIN ROOM button.
    const joinRow = el('div', 'online-joinrow', panel);
    const joinLabel = el('div', 'online-joinlabel', joinRow);
    el('span', 'online-joinrule', joinLabel);
    el('span', 'online-joinlabel-text', joinLabel).textContent = 'HAVE A CODE?';
    el('span', 'online-joinrule online-joinrule-r', joinLabel);
    const joinControls = el('div', 'online-joincontrols', joinRow);
    const codeWrap = el('div', 'online-codewrap', joinControls);
    const lock = el('span', 'online-codelock', codeWrap);
    lock.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1Zm2 0h6V8a3 3 0 0 0-6 0v2Z"/></svg>';
    this.codeInput = el('input', 'online-codeinput', codeWrap);
    this.codeInput.type = 'text';
    this.codeInput.placeholder = 'ROOM CODE';
    this.codeInput.autocapitalize = 'characters';
    this.codeInput.autocomplete = 'off';
    this.codeInput.spellcheck = false;
    this.codeInput.maxLength = 120; // long enough for a pasted invite link
    this.codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.submitCode();
    });
    // The game's global key handlers must not eat lobby-code typing.
    this.codeInput.addEventListener('keydown', (e) => e.stopPropagation());
    this.codeInput.addEventListener('keyup', (e) => e.stopPropagation());
    btn('JOIN ROOM', 'btn btn-gold online-joinbtn', joinControls, () => this.submitCode());

    // M114b: the ONE shared back chevron. The hub is a centred panel with no
    // header row, so it pins to the SCREEN's upper-left corner (the uniform
    // placement) rather than trailing the panel.
    backButton(this.root, () => this.cb.onBack());
  }

  private submitCode(): void {
    const code = parseJoinCode(this.codeInput.value);
    if (!code) {
      // An un-parseable code: shake the input, keep what they typed.
      this.codeInput.classList.remove('shake');
      void this.codeInput.offsetWidth;
      this.codeInput.classList.add('shake');
      return;
    }
    this.codeInput.value = '';
    this.cb.onJoinCode(code);
  }

  /** M140: pending crew/friend activity on the RACE CREW card (0 = none). */
  setCrewBadge(count: number): void {
    if (!this.crewBadge) return;
    this.crewBadge.textContent = count > 9 ? '9+' : String(count);
    this.crewBadge.style.display = count > 0 ? '' : 'none';
  }

  /** `lock` (owner round, 2026-10-02): online racing is part of the paid
   *  game and this player does not own it — the hub still opens, wearing the
   *  lock banner under its header, with the cards dimmed. Flow guards every
   *  card's action (they route to the purchase screen). */
  show(lock?: LockCtaOptions): void {
    this.lockEl?.remove();
    this.lockEl = null;
    if (lock) {
      this.lockEl = lockCta(this.head.parentElement as HTMLElement, {
        ...lock,
        className: 'online-lockcta',
      });
      this.head.after(this.lockEl);
    }
    this.root.classList.toggle('online-locked', !!lock);
    this.root.style.display = '';
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}
